import { extendObservable, reaction, action, computed } from "mobx";
import { Exclude, Expose, classToPlain } from "class-transformer";
import { simplyDeserialize } from "../../common_components/serialization/common";
import { safeMomentDate } from "../../common_components/functions";
import moment from "moment";
import { CHAT_DEFAULT_SUBJECT, CHAT_DEFAULT_BODY, CHAT_STATE } from "../constants";
import groupImg from "../img/group.svg";
import uuid from "node-uuid";

@Exclude()
export default class Conversation {
  constructor(store, id, persisted = true) {
    this.store = store;
    extendObservable(this, {
      @Expose()
      id: id,

      isChat: false,

      subject: "",

      unread: null,

      updatedAt: null,

      lastMessage: {},

      lastSender: {},

      participants: [],

      messages: [],

      messagesNextPage: 1,

      loadingMessages: false
    });

    this.updateDisposer = reaction(
      () => {
        let trackedData = {
          messages: this.messages
        };
        return trackedData;
      },
      () => {
        const sortedMessages = this.sortedMessages;
        if (sortedMessages.length > 0) {
          this.updatedAt = safeMomentDate(sortedMessages[0].updatedAt);
        }
      }
    );
  }

  serialize() {
    let basicSerialize = classToPlain(this);
    return basicSerialize;
  }

  toJson() {
    return this.serialize();
  }

  static deserialize(json, store) {
    let result = simplyDeserialize(Conversation, json);
    result["updatedAt"] = safeMomentDate(json.updatedAt);
    result.store = store;
    if (result.hasMessages) {
      result.setLastMessage(result.sortedMessages[result.messages.length - 1]);
      result.setLastSender(result.sortedMessages[result.messages.length - 1].sender);
    }
    return result;
  }

  updateFromJson(json) {
    Conversation.allowedInstanceProperties.forEach(prop => {
      if (json[prop]) {
        if (["createdAt", "updatedAt", "doneAt"].includes(prop)) {
          this[prop] = safeMomentDate(json[prop]);
        } else {
          this[prop] = json[prop];
        }
      }
    });
  }

  participantNames() {
    return this.participants.map(participant => participant.name).join(", ");
  }

  // Return a participant different from the current user
  // or the current user if there are no more participants
  // in the conversation
  anyParticipant() {
    let participant = this.participants.find(el => el.id != this.store.rootStore.user.id);
    if (!participant) {
      participant = this.store.rootStore.user;
    }
    return participant;
  }

  @computed
  get name() {
    let name = "";
    const currentUser = this.store.rootStore.user;
    if (this.isGroup) {
      name = this.subject;
    } else {
      let participant = this.anyParticipant();
      name = participant.name;
    }
    return name;
  }

  @computed
  get isGroup() {
    return this.subject != CHAT_DEFAULT_SUBJECT;
  }

  @computed
  get icon() {
    if (this.isGroup) {
      return groupImg;
    }
    return this.anyParticipant().profilePhotoUrl;
  }

  @computed
  get formattedLastMessage() {
    let message = this.lastMessage.body;
    if (this.isGroup) {
      if (message == CHAT_DEFAULT_BODY) {
        message = "";
      } else {
        message = this.lastSender.name + ": " + message;
      }
    }
    return message;
  }

  @computed
  get hasLastMessage() {
    return this.lastMessage && this.lastSender;
  }

  isParticipant(participantId) {
    return this.participants.find(el => el.id == participantId) != undefined;
  }

  isPairConversationWith(participantId) {
    return this.participants.length == 2 && this.isParticipant(participantId) && !this.isGroup;
  }

  @action openConversation() {
    this.store.showConversation(this);
    this.setLoadingMessages(true);
    this.setRead();
    this.store.fetchConversation(this).then(data => {
      if (data) {
        this.appendMessages(data.messages);
        this.setMessagesNextPage(data.nextPage);
        this.setLoadingMessages(false);
      }
    });
  }

  @action
  setRead() {
    this.unread = false;
  }

  @action
  setUnread() {
    this.unread = true;
  }

  // val should be boolean
  @action
  setLoadingMessages(val) {
    this.loadingMessages = val;
  }

  // Return true if the message is appended and false otherwise
  @action
  appendMessage(message) {
    return this.appendMessages([message]);
  }

  // Return true if at least one new message has been appended
  @action
  appendMessages(messages) {
    const nonDuplicatedMessages = messages.filter(m => !this.messages.some(el => el.id == m.id));
    nonDuplicatedMessages.forEach(m => {
      this.messages.push(m);
    });
    return nonDuplicatedMessages.length > 0;
  }

  @action
  setMessagesNextPage(nextPage) {
    this.messagesNextPage = nextPage;
  }

  @computed
  get sortedMessages() {
    return this.messages.sort(
      (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
    );
  }

  @action
  loadMoreMessages() {
    if (!this.loadingMessages) {
      this.setLoadingMessages(true);
      this.store.fetchConversation(this).then(data => {
        if (data) {
          this.appendMessages(data.messages);
          this.setMessagesNextPage(data.nextPage);
          this.setLoadingMessages(false);
        }
      });
    }
  }

  @computed
  get hasUnretrievedMessages() {
    return this.messagesNextPage != undefined;
  }

  @computed
  get hasMessages() {
    return this.messages.length > 0;
  }

  @action
  reply(text) {
    // this.playNewMessageSound();
    let message = {
      id: uuid.v4(),
      body: text,
      sender: {
        id: this.store.rootStore.user.id,
        name: this.store.rootStore.user.name,
        profilePhotoUrl: this.store.rootStore.user.profilePhotoUrl
      },
      updatedAt: new Date().getTime()
    };
    const nextMessageIndex = this.messages.push(message);
    this.store
      .reply(this, message)
      .then(persistedMessage => {
        this.replaceMessage(nextMessageIndex - 1, persistedMessage);
        this.setLastSender(message.sender);
        this.setLastMessage(message);
      })
      .catch(err => console.log(err));
  }

  @action
  replaceMessage(messageIndex, newMessage) {
    this.messages[messageIndex] = newMessage;
  }

  @action
  setLastMessage(message) {
    this.lastMessage = message;
  }

  @action
  setLastSender(sender) {
    this.lastSender = sender;
  }

  @action
  appendMessageFromActionCable(message) {
    const appended = this.appendMessage(message);
    if (appended) {
      const uiStore = this.store.rootStore.uiStore;
      //console.log("Play new message sound");
      uiStore.playNewMessageSound();
      if (uiStore.viewState != CHAT_STATE.CONVERSATION || !this.isCurrentConversation) {
        this.setUnread();
      }
    }
  }

  @computed
  get isCurrentConversation() {
    return this.store.currentConversation == this;
  }
}

Conversation.allowedInstanceProperties = [
  "isChat",
  "lastMessage",
  "lastSender",
  "participants",
  "subject",
  "unread",
  "updatedAt",
  "messages"
];
